<script setup>
import { watch } from 'vue'
import { addKeyframes } from './animation'
import carImage from '../../../../assets/images/thx_to_you_counter/car.webp'
import frontRim from '../../../../assets/images/thx_to_you_counter/wheel-front-rim.webp'
import frontHub from '../../../../assets/images/thx_to_you_counter/wheel-front-hub.webp'
import rearRim from '../../../../assets/images/thx_to_you_counter/wheel-rear-rim.webp'
import rearHub from '../../../../assets/images/thx_to_you_counter/wheel-rear-hub.webp'

const DRIVE_SECONDS = 2.6
const BRAKE_START = DRIVE_SECONDS * 0.28
const DRIVE_OFF = DRIVE_SECONDS * 0.62
const SMOKE_CLEAR_START = 3.5
const PITCH = [
  [0, 0],
  [26, 0],
  [31, -1.8],
  [36, -2.2],
  [42, 0.8],
  [48, -0.3],
  [54, 0],
  [62, 0],
  [72, 1.3],
  [100, 0.6],
]
const wheels = [
  { name: 'front', rim: frontRim, hub: frontHub, left: 43.47, top: 63.13, width: 13.28, height: 32.66, squash: 0.778, turnsIn: -636, turnsOut: -1381 },
  { name: 'rear', rim: rearRim, hub: rearHub, left: 93.13, top: 57.98, width: 6.6, height: 27.07, squash: 0.466, turnsIn: -753, turnsOut: -1635 },
]
const puffs = [
  { x: 77, at: 0, driftX: 12 },
  { x: 50, at: 0.05, driftX: -3 },
  { x: 66, at: 0.24, driftX: 4 },
  { x: 34, at: 0.4, driftX: -14 },
  { x: 58, at: 0.62, driftX: 3 },
  { x: 42, at: 0.81, driftX: -9 },
  { x: 72, at: 1, driftX: 10 },
].map((puff, index) => {
  const delay = BRAKE_START + 0.09 + puff.at * 0.74
  return { ...puff, y: 45, size: 18 + (index % 3) * 2, delay, duration: SMOKE_CLEAR_START + puff.x * 0.012 - delay }
})

const props = defineProps({
  timeline: { type: Object, required: true },
  stageStyle: { type: Object, required: true },
  stageUnit: { type: Number, required: true },
  isCornerScreen: { type: Boolean, required: true },
})
const emit = defineEmits(['ready'])
const carElement = ref(null)
const carBodyElement = ref(null)
const wheelElements = {}
const puffElements = []
const timeline = props.timeline

watch(
  () => [props.stageStyle.width, props.stageStyle.height, props.stageStyle.left, props.stageStyle.top],
  () => {
    const progress = timeline.totalProgress()
    timeline.invalidate().totalProgress(progress)
  }
)

const wheelStyle = (wheel) => ({
  left: `${wheel.left}%`,
  top: `${wheel.top}%`,
  width: `${wheel.width}%`,
  height: `${wheel.height}%`,
  '--squash': wheel.squash,
  '--disc-width': `${((wheel.height * 689) / (wheel.width * 1318)) * 100}%`,
})

const carWidthInUnits = () => (props.stageUnit ? carElement.value.clientWidth / props.stageUnit : 60)
const toPercent = (stageWidth) => (stageWidth / carWidthInUnits()) * 100
const centeredCarLeft = () => 50 - carWidthInUnits() / 2
const carStyle = computed(() => ({
  top: `${props.stageUnit * 19}px`,
}))
const smokeLayerStyle = computed(() => ({
  '--smoke-blur': `${props.stageUnit * 0.5}px`,
}))
const puffStyle = (puff) => ({
  left: `${(puff.x - puff.size / 2) * props.stageUnit}px`,
  top: `${(puff.y - puff.size / 2) * props.stageUnit}px`,
  width: `${puff.size * props.stageUnit}px`,
  height: `${puff.size * props.stageUnit}px`,
})

const preloadCarAssets = () => {
  const images = [carImage, frontRim, frontHub, rearRim, rearHub]
  const loaded = Promise.all(
    images.map(
      (src) =>
        new Promise((resolve) => {
          const image = new Image()
          image.onload = resolve
          image.onerror = resolve
          image.src = src
        })
    )
  )
  return Promise.race([loaded, new Promise((resolve) => setTimeout(resolve, 4000))])
}

onMounted(async () => {
  await preloadCarAssets()
  if (!carElement.value) return

  const stage = carElement.value.parentElement
  const output = stage.parentElement
  const extraDistance = () => Math.max(0, (output.clientWidth - stage.clientWidth) / 2 / (stage.clientWidth / 100))

  puffs.forEach((puff, index) => {
    const element = puffElements[index]
    const { delay, duration } = puff
    const driftX = (puff.driftX / puff.size) * 100
    const driftY = (-20 / puff.size) * 100

    timeline
      .set(element, { opacity: 0, scale: 0.12, xPercent: 0, yPercent: 0, immediateRender: true }, 0)
      .to(element, { opacity: 1, duration: duration * 0.12, ease: 'sine.out' }, delay)
      .to(element, { opacity: 0, duration: duration * 0.3, ease: 'sine.out' }, delay + duration * 0.7)
      .to(element, { scale: 1.6, xPercent: driftX * 0.7, yPercent: driftY * 0.7, duration: duration * 0.4, ease: 'sine.out' }, delay)
      .to(element, { scale: 2.2, duration: duration * 0.3, ease: 'sine.out' }, delay + duration * 0.4)
      .to(element, { scale: 2.6, duration: duration * 0.3, ease: 'sine.out' }, delay + duration * 0.7)
      .to(element, { xPercent: driftX, yPercent: driftY, duration: duration * 0.6, ease: 'sine.out' }, delay + duration * 0.4)
  })

  timeline
    .fromTo(carElement, { xPercent: () => toPercent(100 + extraDistance()) }, { xPercent: toPercent(30), duration: DRIVE_SECONDS * 0.28, ease: 'none' }, 0)
    .to(carElement, { xPercent: () => toPercent(centeredCarLeft()), duration: DRIVE_SECONDS * 0.08, ease: 'power3.out' }, BRAKE_START)
    .to(carElement, { xPercent: () => toPercent(-carWidthInUnits() - 2 - extraDistance()), duration: DRIVE_SECONDS * 0.38, ease: 'power2.in' }, DRIVE_OFF)

  addKeyframes(timeline, carBodyElement, 'rotation', PITCH, DRIVE_SECONDS)
  timeline.to(carBodyElement, { yPercent: -0.39, duration: 0.16, ease: 'sine.inOut', repeat: 7, yoyo: true }, 0)

  wheels.forEach(({ name, turnsIn, turnsOut }) => {
    const rotationIn = () => (turnsIn * (70 + extraDistance())) / 70
    const rotationOut = () => {
      const extra = extraDistance()
      const inRotation = (turnsIn * (70 + extra)) / 70
      return inRotation + ((turnsOut - turnsIn) * (82 + extra)) / 82
    }

    timeline
      .fromTo(wheelElements[name], { rotation: 0 }, { rotation: rotationIn, duration: DRIVE_SECONDS * 0.28, ease: 'none' }, 0)
      .to(wheelElements[name], { rotation: rotationOut, duration: DRIVE_SECONDS * 0.38, ease: 'power2.in' }, DRIVE_OFF)
  })

  emit('ready')
})
</script>

<template>
  <div class="thx-intro pointer-events-none absolute inset-0" :class="{ 'thx-intro--corner': props.isCornerScreen }">
    <div class="thx-intro__stage absolute" :style="stageStyle">
      <svg width="0" height="0" focusable="false" aria-hidden="true">
        <filter id="thx-smoke-filter" x="-20%" y="-20%" width="140%" height="140%">
          <feTurbulence type="fractalNoise" baseFrequency="0.012" numOctaves="3" seed="4" result="noise" />
          <feDisplacementMap in="SourceGraphic" in2="noise" scale="60" />
        </filter>
      </svg>

      <div class="smoke-layer absolute inset-0 z-[5]" :style="smokeLayerStyle" aria-hidden="true">
        <span v-for="(puff, index) in puffs" :key="index" :ref="(element) => (puffElements[index] = element)" class="smoke-puff absolute opacity-0" :style="puffStyle(puff)">
          <span class="smoke-puff__shape" />
        </span>
      </div>

      <div ref="carElement" class="thx-car absolute left-0 z-[6]" :style="carStyle">
        <div class="car-shadow" />
        <div ref="carBodyElement" class="absolute inset-0 origin-[50%_90%]">
          <img :src="carImage" width="1318" height="689" alt="" class="block size-full" />
          <div v-for="wheel in wheels" :key="wheel.name" class="thx-car__wheel" :style="wheelStyle(wheel)">
            <div class="thx-car__disc">
              <img :ref="(element) => (wheelElements[wheel.name] = element)" :src="wheel.rim" alt="" class="thx-car__rim" />
              <img :src="wheel.hub" alt="" class="thx-car__hub" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.thx-intro {
  overflow: hidden;
}

.smoke-puff__shape {
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 9999px;
  background: radial-gradient(circle, rgb(246 238 238) 0%, rgb(246 232 232) 40%, rgb(240 215 215 / 85%) 58%, rgb(240 200 200 / 0%) 72%);
}

.smoke-layer {
  filter: url(#thx-smoke-filter) blur(var(--smoke-blur));
}

.thx-car {
  width: 60%;
  aspect-ratio: 1318 / 689;
}

.thx-intro--corner .thx-car {
  width: 75%;
}

.thx-intro--corner .smoke-puff__shape {
  transform: scale(1.3, 2);
}

.car-shadow {
  position: absolute;
  left: -2%;
  bottom: -4%;
  width: 104%;
  height: 22%;
  background: radial-gradient(ellipse at center, rgb(0 0 0 / 60%), transparent 70%);
  filter: blur(var(--stage-unit));
  transform: rotate(-6deg);
}

.thx-car__wheel {
  position: absolute;
}

.thx-car__disc {
  position: absolute;
  top: 0;
  left: 50%;
  width: var(--disc-width);
  height: 100%;
  transform: translateX(-50%) scaleX(var(--squash));
}

.thx-car__rim,
.thx-car__hub {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}
</style>
